import { IFormContext, arraySearch } from 'rewire-ui';
import DataMutations from '../graphql/Mutations';
import DataQueries from '../graphql/Queries';
import { getTimeFormat, getTimeFormats, setTimeFormat, TimeFormat } from '../utility/DateUtils';
import { getNameSearcher } from '../utility/Searchers';
import mainPageModel from '../main-navigation/MainPageModel';
import DocumentDialogModel, { DocumentSchema } from './DocumentDialogModel';

export const timecardWorkflowSearcher = {
  async search() {
    return await DataQueries.getTimecardWorkflows();
  },
  map(item: any) {
    return item?.name ?? '';
  },
};

export const timeOffRequestWorkflowSearcher = {
  async search() {
    return await DataQueries.getTimeOffRequestWorkflows();
  },
  map(item: any) {
    return item?.name ?? '';
  },
};

const weekDaySearcher = arraySearch(['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']);
const timezoneSearcher = getNameSearcher('timezones');
const countrySearcher = getNameSearcher('countries');

const locationSchema = `
  ${DocumentSchema}
  tz {
    id
    name
  }
  weekStart
  timeFormat
  country {
    id
    name
  }
  state {
    id
    name
  }
  workflow {
    id
    name
    fields
  }
  activeUsers
  activeEmployees
  timeOffRequestWorkflow {
    id
    name
  }
`;

const timeFormatSearcher = {
  async search(): Promise<TimeFormat[]> {
    return getTimeFormats();
  },
  map(item: any) {
    return item?.name;
  },
};

export class LocationDialogModel extends DocumentDialogModel {
  stateSearcher: any;
  constructor() {
    super(
      'Location',
      (ids: string[]) => DataQueries.getLocationsByIds(locationSchema, ids),
      DataMutations.insertLocations,
      DataMutations.updateLocations,
      DataMutations.deleteLocations,
      'locations',
      () => mainPageModel.canAddLocations,
      () => mainPageModel.canEditLocations,
      () => mainPageModel.canDeleteLocations,
    );
  }

  protected otherFields(form: IFormContext) {
    if (!this.stateSearcher) {
      this.stateSearcher = {
        search: async (text: string) => {
          const country = this.form.field.country.value;
          if (!country) return [];
          const results = await DataQueries.getSearch('id name', 'states', text, { field: 'country', eq: country.id }, 20);
          return results;
        },
        map: (item: any): string => {
          return item?.name || '';
        },
      };
    }
    return {
      tz: form.select(timezoneSearcher).editor('auto-complete').label('Timezone').placeholder('enter timezone'),
      weekStart: form.select(weekDaySearcher).label('Start of Work Week').placeholder('enter the start of the work week'),
      timeFormat: form.select(timeFormatSearcher).label('Time Format').placeholder('select time format'),
      country: form.select(countrySearcher).editor('auto-complete').label('Country').placeholder('enter country'),
      state: form.select(this.stateSearcher).editor('auto-complete').label('State/Province').placeholder('enter state or province'),
      workflow: form
        .select(timecardWorkflowSearcher)
        .label('Timecard Workflow')
        .placeholder('enter timecard workflow')
        .disabled(() => this.isNew),
      autoLockTimecard: form
        .boolean()
        .label('Auto Lock Timecards')
        .disabled(() => this.isNew || !this.form.field.workflow.value),
      timeOffRequestWorkflow: form
        .select(timeOffRequestWorkflowSearcher)
        .label('Time Off Workflow')
        .placeholder('enter time off workflow')
        .disabled(() => this.isNew),
      activeUsers: form
        .static()
        .label('Active Users')
        .disabled(() => this.isNew),
      activeEmployees: form
        .static()
        .label('Active Employees')
        .disabled(() => this.isNew),
    };
  }

  mapToForm(obj: ObjectType) {
    const timeFormat: TimeFormat | undefined = getTimeFormat(obj.timeFormat);
    if (timeFormat) obj.timeFormat = timeFormat;
    obj.autoLockTimecard = obj.workflow?.fields?.autoLock || false;
    return obj;
  }

  mapToSave() {
    const obj = super.mapToSave();
    setTimeFormat(obj.timeFormat);

    obj.tz = obj.tz?.id;
    obj.timeFormat = obj.timeFormat?.id;
    obj.country = obj.country?.id;
    obj.state = obj.state?.id;

    if (obj.workflow) {
      obj.workflow.fields = { autoLock: obj.autoLockTimecard };
    }
    if (this.isNew) {
      delete obj.workflow;
      delete obj.timeOffRequestWorkflow;
    }

    delete obj.activeEmployees;
    delete obj.activeUsers;
    delete obj.autoLockTimecard;
    return obj;
  }

  async load(id?: string, isModal = true) {
    await super.load(id, isModal);
    this.state.enable = true;
  }
}

const locationDialogModel = new LocationDialogModel();
export default locationDialogModel;
